<script>
import lazadaLogo from "@/assets/images/marketplace/Lazada-03.png";
import shopeeLogo from "@/assets/images/marketplace/Shopee.png";

export default {
  name: "MarketplaceChannelTabs",
  props: {
    channels: {
      type: Array,
      default: () => []
    },
    activeId: {
      type: [String, Number],
      default: null
    },
    lockedId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ["select"],
  computed: {
    tabGridStyle() {
      return {
        gridTemplateColumns: `repeat(${Math.max(
          this.channels.length,
          1
        )}, minmax(64px, 1fr))`
      };
    }
  },
  methods: {
    channelId(channel) {
      return channel.salesChannelId ?? channel.sale_channel_id ?? channel.id;
    },
    channelName(channel) {
      const marketplaceName =
        typeof channel.marketplace === "string" ? channel.marketplace : "";
      return (
        marketplaceName ||
        channel.sale_channel_name ||
        channel.name ||
        channel.shopName ||
        this.$t("marketplace_channel")
      );
    },
    channelImage(channel) {
      if (channel.marketplaceUrl || channel.image_url || channel.logo) {
        return channel.marketplaceUrl || channel.image_url || channel.logo;
      }

      const name = this.channelName(channel).toLowerCase();
      if (name.includes("lazada")) return lazadaLogo;
      if (name.includes("shopee")) return shopeeLogo;
      return "";
    },
    channelFallback(channel) {
      const name = this.channelName(channel).toLowerCase();
      if (name.includes("tiktok")) return "TT";
      if (name.includes("lazada")) return "Laz";
      return "";
    },
    isActive(channel) {
      return String(this.channelId(channel)) === String(this.activeId);
    },
    isDisabled(channel) {
      if (this.lockedId == null || this.lockedId === "") return false;
      return String(this.channelId(channel)) !== String(this.lockedId);
    },
    handleImageError(event) {
      event.currentTarget.hidden = true;
    }
  }
};
</script>

<template>
  <div
    class="marketplace-channel-tabs"
    :style="tabGridStyle"
    data-test="marketplace-connect-channel-tabs"
    role="group"
    :aria-label="$t('marketplace_channel')"
  >
    <button
      v-for="(channel, index) in channels"
      :key="channelId(channel) ?? index"
      type="button"
      class="marketplace-channel-tabs__tab"
      :class="{ 'is-active': isActive(channel) }"
      :disabled="isDisabled(channel)"
      :aria-pressed="isActive(channel)"
      :aria-label="channelName(channel)"
      :data-test="`marketplace-connect-channel-${channelId(channel)}-btn`"
      @click="$emit('select', channel)"
    >
      <span class="marketplace-channel-tabs__icon">
        <span
          v-if="channelFallback(channel)"
          class="marketplace-channel-tabs__fallback"
          aria-hidden="true"
        >
          {{ channelFallback(channel) }}
        </span>
        <font-awesome-icon
          v-else
          :icon="['fas', 'store-alt']"
          class="marketplace-channel-tabs__store-icon"
        />
        <img
          v-if="channelImage(channel)"
          :src="channelImage(channel)"
          :alt="channelName(channel)"
          @error="handleImageError"
        />
      </span>
      <span class="marketplace-channel-tabs__indicator" aria-hidden="true" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.marketplace-channel-tabs {
  display: grid;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}

.marketplace-channel-tabs::-webkit-scrollbar {
  display: none;
}

.marketplace-channel-tabs__tab {
  display: flex;
  min-width: 64px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 0;
  border: 0;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  font: inherit;
}

.marketplace-channel-tabs__tab:disabled {
  cursor: default;
  opacity: 0.58;
  filter: grayscale(1);
}

.marketplace-channel-tabs__tab:focus-visible {
  border-radius: 16px;
  outline: 2px solid var(--primary-color);
  outline-offset: -2px;
}

.marketplace-channel-tabs__icon {
  position: relative;
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 4px solid var(--light-color);
  border-radius: 50%;
  background: var(--disable-color);
  color: var(--font-disable-color);
  box-shadow: 0 4px 10px rgba(39, 31, 43, 0.13);
}

.marketplace-channel-tabs__tab.is-active .marketplace-channel-tabs__icon {
  border-color: var(--primary-color);
  background: var(--secondary-color);
  color: var(--primary-color);
}

.marketplace-channel-tabs__icon img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.marketplace-channel-tabs__fallback {
  font-size: var(--font-title-sm);
  font-weight: 600;
}

.marketplace-channel-tabs__store-icon {
  font-size: var(--font-heading);
}

.marketplace-channel-tabs__indicator {
  width: 20px;
  height: 4px;
  border-radius: 999px;
  background: transparent;
}

.marketplace-channel-tabs__tab.is-active .marketplace-channel-tabs__indicator {
  background: var(--primary-color);
}

@media (max-width: 360px) {
  .marketplace-channel-tabs__tab {
    padding-right: 4px;
    padding-left: 4px;
  }

  .marketplace-channel-tabs__icon {
    width: 50px;
    height: 50px;
  }
}
</style>
